import React, { useState, useEffect, useMemo } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useNavigate } from 'react-router-dom';
import { login } from '../providers/identity';
import { IdentityContext } from './IdentityContext';
import { Roles } from '../enum/Roles';

interface IIdentityContextProviderProps {
  children: React.ReactNode;
}

export const IdentityContextProvider = ({
  children,
}: IIdentityContextProviderProps): JSX.Element => {
  const { authState } = useOktaAuth();
  const navigate = useNavigate();
  const [roles, setRoles] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isSupervisor, setIsSupervisor] = useState<boolean>(false);
  const [isElevated, setIsElevated] = useState<boolean>(false);
  const [identityLoaded, setIdentityLoaded] = useState<boolean>(false);

  const accessToken = authState?.accessToken?.accessToken;

  useEffect(() => {
    if (!authState?.isAuthenticated || !accessToken) return;

    const fetchRoles = async () => {
      try {
        const fetchedRoles = await login(accessToken);
        if (fetchedRoles.length === 0) {
          navigate('/error', {
            state: {
              message: 'You do not have access to this application.',
              title: 'Unauthorized',
            },
          });
          return;
        }
        setRoles(fetchedRoles);
        setIsAdmin(fetchedRoles.includes(Roles.Admin));
        setIsSupervisor(fetchedRoles.includes(Roles.Supervisor));
        setIsElevated(
          fetchedRoles.includes(Roles.Admin) ||
            fetchedRoles.includes(Roles.Supervisor)
        );
      } catch {
        navigate('/error', {
          state: {
            message: 'You do not have access to this application.',
            title: 'Unauthorized',
          },
        });
      } finally {
        setIdentityLoaded(true);
      }
    };

    fetchRoles();
  }, [authState?.isAuthenticated, accessToken]);

  const contextValue = useMemo(
    () => ({ roles, isAdmin, isSupervisor, isElevated, identityLoaded }),
    [roles, isAdmin, isSupervisor, isElevated, identityLoaded]
  );

  return (
    <IdentityContext.Provider value={contextValue}>
      {children}
    </IdentityContext.Provider>
  );
};
